<template>
    <page-template page-id="Credentials" ref="super">

        <document-title :title="$tb('pages.setPass.documentTitle')"></document-title>

        <template v-slot:before v-if="isTitlebarActive">
            <titlebar :text="$tb('pages.setPass.title')"></titlebar>
        </template>

        <div class="ui column">

            <brand-header :text="$tb('pages.setPass.header', {0: $store.state.appName})"
                          :has-extension="hasHeaderExtension"
                          :has-logo="hasBrandLogo">
            </brand-header>

            <p v-html="$t( 'pages.setPass.subheader', {0:$store.state.appName, 1:username})"></p>

            <messages id="Notifications" ref="postman"></messages>
            <label for="username" class="hidden">{{$t('common.label.username')}}</label>
            <input type="text" id="username" name="username" v-model="username" class="hidden">

            <validated-field :show-errors-on="showErrors" :has-any-errors="$v.confirmationCode.$invalid"
                             :validation-rules="[{value: $v.confirmationCode.notEmpty, msg: $t('common.validation.otp.empty')},
                             {value: $v.confirmationCode.hasFormat, showOn: $v.confirmationCode.notEmpty, msg: $t('common.validation.otp.format')}]">
                <tan-input-field id="tan" v-model="$v.confirmationCode.$model" ref="tanInput"
                                 :aria-label-tan-type="$t('aria.common.confirmationCode')" @focusout="otpFocusOut"
                                 :invalid="$v.confirmationCode.$invalid"></tan-input-field>
            </validated-field>

            <validated-field id="password-errors"
                             :show-errors-on="showErrors" :has-any-errors="$v.password.$invalid"
                             :validation-rules="[{value: $v.password.required, msg: this.$t('common.validation.field.empty')},
                                                 {value: $v.password.guidelines, showOn: $v.password.required, msg: $t('common.validation.password.guidelines')},
                                                 {value: $v.password.noInvalidCharacters, showOn: $v.password.guidelines, msg: $t('common.validation.password.invalidCharacter')}]">
                <password-input-field id="password"
                                      autocomplete="new-password"
                                      :label="$t('pages.setPass.newPassword')"
                                      v-model="$v.password.$model"
                                      aria-error-element-id="aria-password-rules-summary password-errors aria-password-status"
                                      :aria-label-text="$t('common.label.password')"
                                      :invalid="$v.password.$invalid && showErrors"></password-input-field>
                <span id="aria-password-rules-summary" style="height:0; overflow:hidden; position:absolute;" v-if="!password">
                    {{ $t('components.password.aria.rules.summary') }}
                </span>
            </validated-field>
            <validated-field
              id="confirm-password-errors"
              v-if="passwordConfirmationEnabled"
              :show-errors-on="showErrors" :has-any-errors="$v.confirmPassword.$invalid"
              :validation-rules="[{value: $v.confirmPassword.requiredIf, msg: $t('common.validation.field.empty')},
                                    {value: $v.confirmPassword.passwordConfirmed, msg: $t('common.validation.password.match')}]">
              <password-input-field id="confirm-password"
                                    autocomplete="off"
                                    :label="$t('common.label.confirmPassword')"
                                    v-model="$v.confirmPassword.$model"
                                    :mandatory="true"
                                    :class="{'input-valid': !$v.confirmPassword.$invalid && $v.confirmPassword.passwordConfirmed}"
                                    aria-error-element-id="aria-confirm-password-rules-summary confirm-password-errors aria-confirm-password-status"
                                    :aria-label-text="$t('common.label.confirmPassword')"
                                    :invalid="$v.confirmPassword.$invalid && showErrors">
              </password-input-field>

              <span id="aria-confirm-password-rules-summary" style="height:0; overflow:hidden; position:absolute;" v-if="!confirmPassword">
                      {{ $t('components.password.aria.rules.summary') }}
              </span>
            </validated-field>
            <div class="pass-meter-container">
              <password-meter
                  v-if="password && !!isPwdStrengthMeterEnabled"
                  :password="password" :first-name="firstname" :last-name="lastname" :user-name="username">
              </password-meter>
              <password-denylist
                  v-if="password && !!pwdDenyListValidationEnabled"
                  :password="password" :first-name="firstname" :last-name="lastname" :user-name="username">
              </password-denylist>
            </div>

            <password-rules :password="password" v-if="!!password"></password-rules>

            <div class="field">
                <button id="confirm" type="submit" class="ui fluid large primary submit button" @click.prevent="onConfirm">
                    {{$t('common.button.confirm')}}
                </button>
            </div>

            <div class="field" v-if="getConfig('resetPass_hasBackButton')">
                <button id="back" @click="goBack" class="ui fluid basic button">
                    {{$t('common.button.back')}}
                </button>
            </div>

            <h4 class="custom-ui header" v-html="$t('pages.setPass.resendCode.header')"></h4>

            <p v-if="isMobileLoginDisabled" v-html="$t('pages.setPass.resendCode.textEmail')"></p>

            <p v-else v-html="$t('pages.setPass.resendCode.text')"></p>

            <div class="field">
                <resend-button id="resend" ref="resendBtn"
                               :value="$t('common.button.resend')"
                               :action="resendOtp"></resend-button>
            </div>
        </div>
    </page-template>
</template>
<script>
import PageTemplate from './template/PageTemplate.vue'
import Titlebar from '../components/Titlebar.vue'
import BrandHeader from '../components/BrandHeader.vue'
import ResendButton from '../components/ResendButton.vue'
import Messages from '../components/Messages.vue'
import TanInputField from '../components/TanInputField.vue'
import PasswordInputField from '../components/LabeledPasswordInputField.vue'
import PasswordRules from '../components/PasswordRules.vue'
import ValidatedField from '../components/ValidatedField.vue'
import { mixin } from '../js/ciam-mixin.js'
import { brand } from '../js/brand.js'
import * as validate from '../js/validation.js'
import { helpers, required, requiredIf } from 'vuelidate/lib/validators'
import PasswordMeter from '../components/PasswordMeter'
import PasswordDenylist from '../components/PasswordDenylist'

export default {
  components: {
    PageTemplate,
    Titlebar,
    BrandHeader,
    TanInputField,
    ResendButton,
    Messages,
    PasswordInputField,
    PasswordRules,
    ValidatedField,
    PasswordMeter,
    PasswordDenylist
  },
  mixins: [mixin, brand],
  data: function () {
    return {
      username: this.$store.state.username,
      confirmationCode: null,
      password: '',
      confirmPassword: '',
      operation: 'pwdset',
      showErrors: false,
      isPwdStrengthMeterEnabled: this.$store.state.isPasswordStrengthMeterEnabled ? this.$store.state.isPasswordStrengthMeterEnabled : false,
      pwdDenyListValidationEnabled: this.$store.state.pwdDenyListValidationEnabled ? this.$store.state.pwdDenyListValidationEnabled : false,
      isMobileLoginDisabled: this.$store.state.isMobileLoginDisabled
    }
  },
  props: {
  },
  validations () {
    return {
      confirmationCode: {
        notEmpty (value) {
          return !!value && value !== '------'
        },
        hasFormat (value) {
          return /[\d]{6}/.test(value) // otp needs to be 6 digits
        }
      },
      password: {
        required,
        guidelines (input) {
          return validate.passwordRulesSatisfied(input)
        },
        noInvalidCharacters (input) {
          return !validate.containsInvalidCharacter(input)
        }
      },
      confirmPassword: {
        requiredIf: requiredIf(() => {
          return this.passwordConfirmationEnabled
        }),
        passwordConfirmed (input) {
          return !helpers.req(input) || input === this.password
        }
      }

    }
  },
  computed: {
    passwordConfirmationEnabled: function () {
      return this.$store.state.isPasswordConfirmationEnabled
    }
  },
  methods: {
    onConfirm: function (e) {
      this.$refs.postman.clear()
      this.showErrors = false

      if (!this.isFormValid()) return

      this.$refs.super.startLoading()

      const payload = this.$api.payloads.buildSetPasswordPayload(this.username, this.confirmationCode, this.password)
      this.$api.login.setPassword(payload, this.handleRoutingResponse, this.onFail)
    },
    isFormValid: function () {
      if (!this.$v.$anyDirty || this.$v.$anyError || this.$v.$invalid) {
        const _this = this

        this.focusFirstErrorField(function () {
          _this.showErrors = true
        })
        return false
      }

      return true
    },
    onFail: function (response) {
      this.onAjaxError(response)
      this.$refs.super.stopLoading()
    },
    goBack: function () {
      window.history.length > 1 ? window.history.back() : this.$router.push('/')
    },
    resendOtp: function () {
      this.$refs.postman.clear()
      this.$refs.super.startLoading()
      this.$api.codes.sendPasswordReset(this.username, this.operation, this.handleResendResponse, this.onFail)
      $('#tan').focus() // FIXME in the future
    },
    handleResendResponse: function () {
      this.$refs.tanInput.reset()// reset the TAN input
      this.$refs.resendBtn.disableActionForTime(30)
      this.$refs.postman.showMessage('info', this.$t('common.msg.resendOk', {
        0: this.$tb('common.label.confirmationCode'),
        1: this.username
      }))
      this.$refs.super.stopLoading()
    },
    checkHideErrors: function () {
      if (this.$v.$invalid) return

      this.showErrors = false
    },
    otpFocusOut: function () {
      $('#password').focus()
    }
  },
  mounted: function () {
    console.log(`Set password [username=${this.username}]`)
  },
  watch: {
    password: {
      handler (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.showErrors = false
          $('#grp_password').siblings('.ui.basic.red').remove()
        }
      }
    },
    $v: {
      handler () {
        this.checkHideErrors()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.pass-meter-container {
  margin-top: 14px;
}

.input-valid {
  border: 2px solid green;
  background-color: #ceffce
}
</style>
